import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  AlertTriangle,
  Calendar,
  Target,
  Activity,
  Zap,
  Filter,
  ArrowUp,
  ArrowDown,
  Minus,
  Search,
  Download,
  RefreshCw,
  BarChart3,
  PieChart,
  Users,
  Layers,
  FileText,
  List,
  FolderOpen
} from 'lucide-react';
import { User as UserType } from '../../types';
import Avatar from '../common/Avatar';
import { todayLocal } from '../../utils/localDate';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';
import { DevOpsTask } from '../../types/devopsTasks';

interface TaskDashboardProps {
  currentUser: UserType;
  onSelectTask: (taskId: string) => void;
  onCreateTask: () => void;
  onViewAllTasks?: () => void;
  onViewMyTasks?: () => void;
}

type FilterOption = 'all' | 'new' | 'assigned' | 'in-progress' | 'testing' | 'completed' | 'rejected';
type PriorityFilter = 'all' | 'critical' | 'high' | 'medium' | 'low';
type TimeFilter = 'all' | 'today' | 'week' | 'month' | 'overdue';

const TaskDashboard: React.FC<TaskDashboardProps> = ({ currentUser, onSelectTask, onCreateTask, onViewAllTasks, onViewMyTasks }) => {
  const [tasks, setTasks] = useState<DevOpsTask[]>([]);
  const [myTasks, setMyTasks] = useState<DevOpsTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterOption>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [currentUser.id]);

  const fetchTasks = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      // Fetch all tasks if DevOps, otherwise fetch user's tasks
      let allTasksData: DevOpsTask[] = [];
      let myTasksData: DevOpsTask[] = [];
      const getTasks = async (url: string): Promise<DevOpsTask[]> => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(await readApiError(res, 'Failed to load tasks'));
        const data = await res.json();
        return Array.isArray(data) ? data : [];
      };
      
      if (currentUser.role === 'devops') {
        allTasksData = await getTasks('/api/tasks');
        setTasks(allTasksData);
      } else {
        // userId+userRole (not just createdBy) so the backend also includes tasks
        // assigned to this user or awaiting their approval, not only ones they created
        // — otherwise a TL/PM/admin who's an approver but not the creator never sees
        // it in their own Pending Approvals widget below.
        myTasksData = await getTasks(`/api/tasks?userId=${currentUser.id}&userRole=${currentUser.role}`);
        setMyTasks(myTasksData);
        allTasksData = myTasksData;
      }

      // Fetch user's own tasks separately if DevOps
      if (currentUser.role === 'devops') {
        myTasksData = await getTasks(`/api/tasks?createdBy=${currentUser.id}`);
        setMyTasks(myTasksData);
      }
      
    } catch (err) {
      console.error('Error fetching tasks:', err);
      toast(err instanceof Error ? err.message : 'Failed to load tasks', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchTasks(true);
  };

  // Calculate statistics with filters
  const dataSet = currentUser.role === 'devops' ? tasks : myTasks;

  // Apply filters
  const filteredTasks = useMemo(() => {
    let filtered = [...dataSet];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(query) ||
        task.description.toLowerCase().includes(query) ||
        String(task.taskNumber).includes(query) ||
        task.createdBy.name.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Time filter
    const now = new Date();
    if (timeFilter === 'today') {
      filtered = filtered.filter(task => {
        const created = new Date(task.createdAt);
        return created.toDateString() === now.toDateString();
      });
    } else if (timeFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(task => new Date(task.createdAt) >= weekAgo);
    } else if (timeFilter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(task => new Date(task.createdAt) >= monthAgo);
    } else if (timeFilter === 'overdue') {
      filtered = filtered.filter(task => {
        if (!task.expectedCompletionDate || ['completed', 'rejected'].includes(task.status)) return false;
        return new Date(task.expectedCompletionDate) < now;
      });
    }

    return filtered;
  }, [dataSet, searchQuery, statusFilter, priorityFilter, timeFilter]);
  
  const stats = {
    total: dataSet.length,
    new: dataSet.filter(t => t.status === 'new').length,
    assigned: dataSet.filter(t => t.status === 'assigned').length,
    inProgress: dataSet.filter(t => t.status === 'in-progress').length,
    testing: dataSet.filter(t => t.status === 'testing').length,
    completed: dataSet.filter(t => t.status === 'completed').length,
    rejected: dataSet.filter(t => t.status === 'rejected').length,
    critical: dataSet.filter(t => t.priority === 'critical' && !['completed', 'rejected'].includes(t.status)).length,
    high: dataSet.filter(t => t.priority === 'high' && !['completed', 'rejected'].includes(t.status)).length,
    medium: dataSet.filter(t => t.priority === 'medium' && !['completed', 'rejected'].includes(t.status)).length,
    low: dataSet.filter(t => t.priority === 'low' && !['completed', 'rejected'].includes(t.status)).length,
  };

  // Calculate time-based stats
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const timeStats = {
    createdToday: dataSet.filter(t => {
      const created = new Date(t.createdAt);
      return created.toDateString() === now.toDateString();
    }).length,
    createdThisWeek: dataSet.filter(t => new Date(t.createdAt) >= weekAgo).length,
    completedThisWeek: dataSet.filter(t => {
      if (t.status !== 'completed' || !t.completedAt) return false;
      return new Date(t.completedAt) >= weekAgo;
    }).length,
    completedThisMonth: dataSet.filter(t => {
      if (t.status !== 'completed' || !t.completedAt) return false;
      return new Date(t.completedAt) >= monthAgo;
    }).length,
    overdue: dataSet.filter(t => {
      if (!t.expectedCompletionDate || ['completed', 'rejected'].includes(t.status)) return false;
      return new Date(t.expectedCompletionDate) < now;
    }).length,
  };

  // Get high priority tasks
  const urgentTasks = dataSet
    .filter(t => ['critical', 'high'].includes(t.priority) && !['completed', 'rejected'].includes(t.status))
    .sort((a, b) => {
      const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    })
    .slice(0, 5);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Task Number', 'Title', 'Status', 'Priority', 'Created By', 'Created At', 'Assigned To', 'Expected Completion'];
    const rows = filteredTasks.map(task => [
      `T-${String(task.taskNumber).padStart(4, '0')}`,
      task.title,
      task.status,
      task.priority,
      task.createdBy.name,
      new Date(task.createdAt).toLocaleString(),
      task.assignedTo?.name || 'Unassigned',
      task.expectedCompletionDate ? new Date(task.expectedCompletionDate).toLocaleDateString() : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_export_${todayLocal()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setTimeFilter('all');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assigned': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'in-progress': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'testing': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-300';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-300';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-300';
      case 'low': return 'text-green-600 bg-green-50 border-green-300';
      default: return 'text-gray-600 bg-gray-50 border-gray-300';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return <ArrowUp className="w-4 h-4" />;
      case 'high': return <ArrowUp className="w-4 h-4" />;
      case 'medium': return <Minus className="w-4 h-4" />;
      case 'low': return <ArrowDown className="w-4 h-4" />;
      default: return <Minus className="w-4 h-4" />;
    }
  };

  const calculateSuccessRate = () => {
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / (stats.completed + stats.rejected)) * 100) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-lg">Loading task dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-screen overflow-hidden">
      {/* Left Sidebar Menu - Fixed */}
      <div className="w-80 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Quick Actions - Moved to Top */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl shadow-lg p-5 text-white sticky top-0 z-10">
            <h3 className="text-lg font-bold mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button
                onClick={onCreateTask}
                className="w-full px-4 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold shadow-md hover:shadow-lg transition-all flex items-center justify-center"
              >
                <Activity className="w-5 h-5 mr-2" />
                Create New Task
              </button>
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset All Filters
              </button>
              <button
                onClick={exportToCSV}
                className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Export to CSV
              </button>
              {onViewAllTasks && (
                <button
                  onClick={onViewAllTasks}
                  className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center justify-center"
                >
                  <List className="w-4 h-4 mr-2" />
                  View All Tasks
                </button>
              )}
              {onViewMyTasks && (
                <button
                  onClick={onViewMyTasks}
                  className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center justify-center"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  View My Tasks
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats Card */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
              Quick Stats
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                <span className="text-sm font-semibold text-gray-700">Total Tasks</span>
                <span className="text-xl font-bold text-blue-600">{stats.total}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                <span className="text-sm font-semibold text-gray-700">Completed</span>
                <span className="text-xl font-bold text-green-600">{stats.completed}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                <span className="text-sm font-semibold text-gray-700">Active</span>
                <span className="text-xl font-bold text-purple-600">{stats.inProgress + stats.testing}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                <span className="text-sm font-semibold text-gray-700">High Priority</span>
                <span className="text-xl font-bold text-orange-600">{stats.critical + stats.high}</span>
              </div>
            </div>
          </div>

        {/* Status Filter Menu */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Filter className="w-5 h-5 mr-2 text-blue-600" />
            Filter by Status
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'all' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Layers className="w-4 h-4 mr-2" />
                All Tasks
              </span>
              <span className="text-sm">{stats.total}</span>
            </button>
            <button
              onClick={() => setStatusFilter('new')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'new' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Zap className="w-4 h-4 mr-2" />
                New
              </span>
              <span className="text-sm">{stats.new}</span>
            </button>
            <button
              onClick={() => setStatusFilter('assigned')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'assigned' 
                  ? 'bg-indigo-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Assigned
              </span>
              <span className="text-sm">{stats.assigned}</span>
            </button>
            <button
              onClick={() => setStatusFilter('in-progress')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'in-progress' 
                  ? 'bg-purple-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                In Progress
              </span>
              <span className="text-sm">{stats.inProgress}</span>
            </button>
            <button
              onClick={() => setStatusFilter('testing')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'testing' 
                  ? 'bg-cyan-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Activity className="w-4 h-4 mr-2" />
                Testing
              </span>
              <span className="text-sm">{stats.testing}</span>
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'completed' 
                  ? 'bg-green-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Completed
              </span>
              <span className="text-sm">{stats.completed}</span>
            </button>
            <button
              onClick={() => setStatusFilter('rejected')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                statusFilter === 'rejected' 
                  ? 'bg-red-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <XCircle className="w-4 h-4 mr-2" />
                Rejected
              </span>
              <span className="text-sm">{stats.rejected}</span>
            </button>
          </div>
        </div>

        {/* Priority Filter Menu */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Filter by Priority
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => setPriorityFilter('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                priorityFilter === 'all' 
                  ? 'bg-gray-700 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Layers className="w-4 h-4 mr-2" />
                All Priorities
              </span>
              <span className="text-sm">{stats.critical + stats.high + stats.medium + stats.low}</span>
            </button>
            <button
              onClick={() => setPriorityFilter('critical')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                priorityFilter === 'critical' 
                  ? 'bg-red-600 text-white shadow-md' 
                  : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
              }`}
            >
              <span className="flex items-center">
                <ArrowUp className="w-4 h-4 mr-2" />
                Critical
              </span>
              <span className="text-sm">{stats.critical}</span>
            </button>
            <button
              onClick={() => setPriorityFilter('high')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                priorityFilter === 'high' 
                  ? 'bg-orange-600 text-white shadow-md' 
                  : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
              }`}
            >
              <span className="flex items-center">
                <ArrowUp className="w-4 h-4 mr-2" />
                High
              </span>
              <span className="text-sm">{stats.high}</span>
            </button>
            <button
              onClick={() => setPriorityFilter('medium')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                priorityFilter === 'medium' 
                  ? 'bg-yellow-600 text-white shadow-md' 
                  : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-200'
              }`}
            >
              <span className="flex items-center">
                <Minus className="w-4 h-4 mr-2" />
                Medium
              </span>
              <span className="text-sm">{stats.medium}</span>
            </button>
            <button
              onClick={() => setPriorityFilter('low')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                priorityFilter === 'low' 
                  ? 'bg-green-600 text-white shadow-md' 
                  : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
              }`}
            >
              <span className="flex items-center">
                <ArrowDown className="w-4 h-4 mr-2" />
                Low
              </span>
              <span className="text-sm">{stats.low}</span>
            </button>
          </div>
        </div>

        {/* Time Filter Menu */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
            Filter by Time
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => setTimeFilter('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                timeFilter === 'all' 
                  ? 'bg-blue-600 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Layers className="w-4 h-4 mr-2" />
                All Time
              </span>
            </button>
            <button
              onClick={() => setTimeFilter('today')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                timeFilter === 'today' 
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Today
              </span>
              <span className="text-sm">{timeStats.createdToday}</span>
            </button>
            <button
              onClick={() => setTimeFilter('week')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                timeFilter === 'week' 
                  ? 'bg-indigo-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                This Week
              </span>
              <span className="text-sm">{timeStats.createdThisWeek}</span>
            </button>
            <button
              onClick={() => setTimeFilter('month')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                timeFilter === 'month' 
                  ? 'bg-purple-500 text-white shadow-md' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                This Month
              </span>
            </button>
            {timeStats.overdue > 0 && (
              <button
                onClick={() => setTimeFilter('overdue')}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg font-semibold transition-all ${
                  timeFilter === 'overdue' 
                    ? 'bg-red-600 text-white shadow-md' 
                    : 'bg-red-50 text-red-700 hover:bg-red-100 border-2 border-red-300'
                }`}
              >
                <span className="flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Overdue
                </span>
                <span className="text-sm font-bold">{timeStats.overdue}</span>
              </button>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Main Content Area - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <Target className="w-10 h-10 mr-3" />
              Task Management Dashboard
            </h1>
            <p className="mt-2 opacity-90 text-blue-100">
              {currentUser.role === 'devops' ? 'Comprehensive overview of all DevOps tasks' : 'Your personal task overview and metrics'}
            </p>
            <div className="flex items-center space-x-4 mt-3">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                <Users className="w-4 h-4 inline mr-1" />
                {currentUser.name}
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                <Layers className="w-4 h-4 inline mr-1" />
                {currentUser.role.toUpperCase()}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-all flex items-center"
            >
              <Download className="w-5 h-5 mr-2" />
              Export CSV
            </button>
            <button
              onClick={onCreateTask}
              className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-semibold shadow-lg hover:shadow-xl transition-all flex items-center"
            >
              <Activity className="w-5 h-5 mr-2" />
              Create Task
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by task number, title, description, or creator..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center transition-all ${
              showFilters ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as FilterOption)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="assigned">Assigned</option>
                <option value="in-progress">In Progress</option>
                <option value="testing">Testing</option>
                <option value="completed">Completed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Time Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Time Period</label>
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {/* Reset Button */}
            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-all"
              >
                Reset Filters
              </button>
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {(searchQuery || statusFilter !== 'all' || priorityFilter !== 'all' || timeFilter !== 'all') && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <FileText className="w-4 h-4" />
                <span className="font-semibold">
                  Showing {filteredTasks.length} of {dataSet.length} tasks
                </span>
                {searchQuery && <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Search: "{searchQuery}"</span>}
                {statusFilter !== 'all' && <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">Status: {statusFilter}</span>}
                {priorityFilter !== 'all' && <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">Priority: {priorityFilter}</span>}
                {timeFilter !== 'all' && <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">Time: {timeFilter}</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Analytics Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Completion Trend */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-green-600" />
            Completion Rate
          </h3>
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center">
              <svg className="w-32 h-32">
                <circle cx="64" cy="64" r="56" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                <circle
                  cx="64"
                  cy="64"
                  r="56"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="8"
                  strokeDasharray={`${(calculateSuccessRate() / 100) * 352} 352`}
                  strokeLinecap="round"
                  transform="rotate(-90 64 64)"
                />
              </svg>
              <div className="absolute">
                <span className="text-3xl font-bold text-gray-900">{calculateSuccessRate()}%</span>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              {stats.completed} completed / {stats.completed + stats.rejected} finished
            </p>
          </div>
        </div>

        {/* Task Distribution */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2 text-blue-600" />
            Status Distribution
          </h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">New</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500" style={{ width: `${stats.total ? (stats.new / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-900 w-8">{stats.new}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">In Progress</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: `${stats.total ? (stats.inProgress / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-900 w-8">{stats.inProgress}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Testing</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500" style={{ width: `${stats.total ? (stats.testing / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-900 w-8">{stats.testing}</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Completed</span>
              <div className="flex items-center space-x-2">
                <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500" style={{ width: `${stats.total ? (stats.completed / stats.total) * 100 : 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-900 w-8">{stats.completed}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Workload Summary */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
            Workload Analysis
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-700">Active Tasks</span>
                <span className="text-lg font-bold text-purple-600">{stats.inProgress + stats.testing}</span>
              </div>
              <p className="text-xs text-gray-600">Currently being worked on</p>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-700">Pending</span>
                <span className="text-lg font-bold text-blue-600">{stats.new + stats.assigned}</span>
              </div>
              <p className="text-xs text-gray-600">Awaiting action</p>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-700">Avg. Completion</span>
                <span className="text-lg font-bold text-green-600">
                  {timeStats.completedThisMonth > 0 ? Math.round(timeStats.completedThisMonth / 4) : 0}/week
                </span>
              </div>
              <p className="text-xs text-gray-600">Based on this month</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {/* Total Tasks */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <Activity className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.total}</span>
          </div>
          <p className="text-sm font-semibold opacity-90">Total Tasks</p>
          <p className="text-xs opacity-75 mt-1">{currentUser.role === 'devops' ? 'All tasks' : 'Created by you'}</p>
        </div>

        {/* New Tasks */}
        <div className="bg-white rounded-xl shadow-lg p-5 border-2 border-blue-200">
          <div className="flex items-center justify-between mb-3">
            <Zap className="w-8 h-8 text-blue-600" />
            <span className="text-3xl font-bold text-blue-900">{stats.new}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700">New</p>
          <p className="text-xs text-gray-500 mt-1">Unassigned</p>
        </div>

        {/* In Progress */}
        <div className="bg-white rounded-xl shadow-lg p-5 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <TrendingUp className="w-8 h-8 text-purple-600" />
            <span className="text-3xl font-bold text-purple-900">{stats.inProgress}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700">In Progress</p>
          <p className="text-xs text-gray-500 mt-1">Active work</p>
        </div>

        {/* Testing */}
        <div className="bg-white rounded-xl shadow-lg p-5 border-2 border-cyan-200">
          <div className="flex items-center justify-between mb-3">
            <Filter className="w-8 h-8 text-cyan-600" />
            <span className="text-3xl font-bold text-cyan-900">{stats.testing}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700">Testing</p>
          <p className="text-xs text-gray-500 mt-1">Under review</p>
        </div>

        {/* Completed */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.completed}</span>
          </div>
          <p className="text-sm font-semibold opacity-90">Completed</p>
          <p className="text-xs opacity-75 mt-1">Successfully done</p>
        </div>

        {/* Rejected */}
        <div className="bg-white rounded-xl shadow-lg p-5 border-2 border-red-200">
          <div className="flex items-center justify-between mb-3">
            <XCircle className="w-8 h-8 text-red-600" />
            <span className="text-3xl font-bold text-red-900">{stats.rejected}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700">Rejected</p>
          <p className="text-xs text-gray-500 mt-1">Not completed</p>
        </div>
      </div>

      {/* Priority & Time Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Breakdown */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Priority Breakdown
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center space-x-3">
                {getPriorityIcon('critical')}
                <span className="font-semibold text-gray-900">Critical</span>
              </div>
              <span className="text-2xl font-bold text-red-600">{stats.critical}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center space-x-3">
                {getPriorityIcon('high')}
                <span className="font-semibold text-gray-900">High</span>
              </div>
              <span className="text-2xl font-bold text-orange-600">{stats.high}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center space-x-3">
                {getPriorityIcon('medium')}
                <span className="font-semibold text-gray-900">Medium</span>
              </div>
              <span className="text-2xl font-bold text-yellow-600">{stats.medium}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-3">
                {getPriorityIcon('low')}
                <span className="font-semibold text-gray-900">Low</span>
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.low}</span>
            </div>
          </div>
        </div>

        {/* Time-based Stats */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-blue-600" />
            Activity Timeline
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Created Today</p>
                <p className="text-xs text-gray-600">New tasks created</p>
              </div>
              <span className="text-2xl font-bold text-blue-600">{timeStats.createdToday}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Created This Week</p>
                <p className="text-xs text-gray-600">Last 7 days</p>
              </div>
              <span className="text-2xl font-bold text-indigo-600">{timeStats.createdThisWeek}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div>
                <p className="font-semibold text-gray-900">Completed This Week</p>
                <p className="text-xs text-gray-600">Last 7 days</p>
              </div>
              <span className="text-2xl font-bold text-green-600">{timeStats.completedThisWeek}</span>
            </div>
            {timeStats.overdue > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-2 border-red-300">
                <div>
                  <p className="font-semibold text-gray-900">Overdue Tasks</p>
                  <p className="text-xs text-red-600">⚠️ Needs attention</p>
                </div>
                <span className="text-2xl font-bold text-red-600">{timeStats.overdue}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending Approvals - Show for TL, PM, Admin */}
      {(['tl', 'pm', 'admin'].includes(currentUser.role)) && (
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl shadow-lg border-2 border-yellow-300 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
            ⏳ Pending Approvals
          </h3>
          {(() => {
            const pendingApprovals = dataSet.filter(task => 
              task.requiresApproval && 
              task.approvalStatus === 'pending' &&
              task.approvers?.some(approver => approver.id === currentUser.id)
            );
            
            if (pendingApprovals.length === 0) {
              return (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3 opacity-50" />
                  <p className="text-gray-600 font-medium">No pending approvals</p>
                  <p className="text-sm text-gray-500 mt-1">All tasks reviewed!</p>
                </div>
              );
            }

            return (
              <div className="space-y-3">
                {pendingApprovals.map((task) => (
                  <div
                    key={task._id}
                    onClick={() => task._id && onSelectTask(task._id)}
                    className="p-4 border-l-4 border-yellow-500 bg-white rounded-lg hover:shadow-md cursor-pointer transition-all animate-pulse hover:animate-none"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono font-bold text-gray-700">
                          T-{String(task.taskNumber).padStart(4, '0')}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${getPriorityColor(task.priority)}`}>
                          {task.priority.toUpperCase()}
                        </span>
                        <span className="px-2 py-1 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">
                          ⏳ AWAITING APPROVAL
                        </span>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mb-1">{task.title}</p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-600 flex items-center gap-1"><Avatar name={task.createdBy.name} userId={task.createdBy.id} size="xs" />Created by {task.createdBy.name}</p>
                      <p className="text-xs font-semibold text-yellow-700">👉 Click to review & approve</p>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Urgent Tasks & Recent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Urgent Tasks */}
        {urgentTasks.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-red-600" />
              High Priority Tasks ({urgentTasks.length})
            </h3>
            <div className="space-y-3">
              {urgentTasks.map((task) => (
                <div
                  key={task._id}
                  onClick={() => task._id && onSelectTask(task._id)}
                  className="p-4 border-l-4 border-red-500 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono font-bold text-gray-700">
                        T-{String(task.taskNumber).padStart(4, '0')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${getPriorityColor(task.priority)}`}>
                        {task.priority.toUpperCase()}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(task.status)}`}>
                      {task.status.replace('-', ' ').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{task.title}</p>
                  <p className="text-xs text-gray-600 flex items-center gap-1"><Avatar name={task.createdBy.name} userId={task.createdBy.id} size="xs" />Created by {task.createdBy.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Performance Metrics */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
        <h3 className="text-2xl font-bold mb-6">Performance Metrics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <p className="text-5xl font-bold mb-2">{calculateSuccessRate()}%</p>
            <p className="text-sm opacity-90">Success Rate</p>
          </div>
          <div className="text-center">
            <p className="text-5xl font-bold mb-2">{stats.total}</p>
            <p className="text-sm opacity-90">Total Tasks</p>
          </div>
          <div className="text-center">
            <p className="text-5xl font-bold mb-2">{timeStats.completedThisMonth}</p>
            <p className="text-sm opacity-90">Completed This Month</p>
          </div>
          <div className="text-center">
            <p className="text-5xl font-bold mb-2">{stats.inProgress + stats.testing}</p>
            <p className="text-sm opacity-90">Active Tasks</p>
          </div>
        </div>
      </div>
      {/* End Main Content Inner wrapper */}
        </div>
      {/* End Main Content Area (flex-1 overflow-y-auto) */}
      </div>
    {/* End Flex Container (flex gap-6 h-screen overflow-hidden) */}
    </div>
  );
};

export default TaskDashboard;
